import {
  AuthControllerApi,
  CertificateControllerApi,
  Configuration,
  ContactControllerApi,
  OrderControllerApi,
  RequestContext,
  ResponseContext,
  StockControllerApi
} from "../api";
import {
  bootstrapAuth,
  getAccessToken,
  handleUnauthorizedResponse,
  tryRefreshToken
} from "../auth/authService";
import { getConfig } from "../config";

const basePath = getConfig().API_BASE_PATH;

let isRefreshing = false;
let refreshPromise: Promise<boolean> | null = null;

async function refreshOnce(): Promise<boolean> {
  if (isRefreshing && refreshPromise) {
    return refreshPromise;
  }
  isRefreshing = true;
  refreshPromise = tryRefreshToken().then((result) => {
    isRefreshing = false;
    refreshPromise = null;
    return result !== null;
  });
  return refreshPromise;
}

const config = new Configuration({
  basePath,
  credentials: "include",
  middleware: [
    {
      async pre(context: RequestContext) {
        // Wait for the session to be restored from the refresh cookie
        await bootstrapAuth();

        const token = getAccessToken();
        if (token) {
          const headers = new Headers(context.init.headers);
          headers.set("Authorization", `Bearer ${token}`);
          return {
            url: context.url,
            init: { ...context.init, headers }
          };
        }
        return undefined;
      },
      async post(context: ResponseContext) {
        if (context.response.status === 401) {
          // Try to refresh the token once
          const refreshed = await refreshOnce();
          if (refreshed) {
            // Retry the original request with the new token
            const token = getAccessToken();
            const headers = new Headers(context.init.headers);
            if (token) {
              headers.set("Authorization", `Bearer ${token}`);
            }
            return fetch(context.url, { ...context.init, headers });
          }

          // Refresh failed — user must log in again
          handleUnauthorizedResponse();
        }

        if (context.response.status === 403) {
          handleUnauthorizedResponse();
        }

        return Promise.resolve(context.response);
      }
    }
  ]
});

export const authApi = new AuthControllerApi(config);
export const orderApi = new OrderControllerApi(config);
export const contactApi = new ContactControllerApi(config);
export const stockApi = new StockControllerApi(config);
export const certificateApi = new CertificateControllerApi(config);
